import { retrieveMemberAffiliationDetails } from "@/common/hook/useHelper";
import { getEntityId } from "@/common/memberDashboardRouting";
import {
  canAccessProductServices,
  canAccessVoting,
  canAccessAsocoins,
  isDevelopmentEnvironment,
} from "@/common/featureAccess";
import { isOrganization } from "@/share/validateOrganization";
import asocoinsSidebarIcon from "@/assets/images/asocoins/coin-clatter-svgrepo-com.svg";
import { getCommunityLabel } from "@/share/communityLabel";

const ACADEMY_ROUTE = process.env.VUE_APP_ACADEMY_URL || "";

export const MEMBERS_NAVIGATION = [
  {
    title: "Inicio",
    icon: "mdi-view-dashboard",
    route: "/member-v2/home",
    disabled: false,
  },
  {
    title: "Mi Perfil",
    icon: "mdi-account-circle-outline",
    route: "/member-v2/profile",
    disabled: false,
  },
  {
    title: "Autodiagnóstico SAC",
    icon: "mdi-clipboard-pulse-outline",
    route: "/sac",
    disabled: false,
    devOnly: true,
  },
  {
    title: "Histórico y Pagos",
    icon: "mdi-book-open-page-variant",
    route: "/member-v2/history",
    disabled: false,
  },
  {
    title: "Mis Asocoins",
    icon: "mdi-coins",
    iconImage: asocoinsSidebarIcon,
    route: "/member-v2/asocoins",
    disabled: false,
    asocoinsOnly: true,
  },
  {
    title: "Submiembros",
    icon: "mdi-account-multiple",
    route: "/member-v2/affiliates",
    disabled: false,
  },
  {
    title: "Eventos",
    icon: "mdi-calendar-multiple",
    route: "/member-v2/events",
    disabled: false,
  },
  {
    title: "Mis Votaciones",
    icon: "mdi-vote-outline",
    route: "/member-v2/votaciones",
    disabled: false,
    prodAsofomOnly: true,
  },
  {
    title: "Mi Comunidad",
    icon: "mdi-account-group-outline",
    route: "/member-v2/community",
    disabled: false,
  },
  {
    title: "Mis Beneficios",
    icon: "mdi-hand-coin-outline",
    route: "/member-v2/benefits",
    disabled: false,
  },
  {
    title: "Productos y Servicios",
    icon: "mdi-shopping-outline",
    route: "/member-v2/products",
    disabled: false,
  },
  {
    title: "Academia",
    icon: "mdi-school",
    route: ACADEMY_ROUTE,
    disabled: false,
    external: true,
    devOnly: true,
  },
];

export const MEMBERSHIP_USERS_NAVIGATION = [
  {
    title: "Inicio",
    icon: "mdi-view-dashboard",
    route: "/member-v2/home",
    disabled: false,
  },
  {
    title: "Mi Perfil",
    icon: "mdi-account-circle-outline",
    route: "/member-v2/profile",
    disabled: false,
  },
  {
    title: "Autodiagnóstico SAC",
    icon: "mdi-clipboard-pulse-outline",
    route: "/sac",
    disabled: false,
    devOnly: true,
  },
  {
    title: "Histórico",
    icon: "mdi-book-open-page-variant",
    route: "/member-v2/history",
    disabled: false,
  },
  {
    title: "Mis Asocoins",
    icon: "mdi-coins",
    iconImage: asocoinsSidebarIcon,
    route: "/member-v2/asocoins",
    disabled: false,
    asocoinsOnly: true,
  },
  {
    title: "Eventos",
    icon: "mdi-calendar-multiple",
    route: "/member-v2/events",
    disabled: false,
  },
  {
    title: "Mis Votaciones",
    icon: "mdi-vote-outline",
    route: "/member-v2/votaciones",
    disabled: false,
    prodAsofomOnly: true,
  },
  {
    title: "Mi Comunidad",
    icon: "mdi-account-group-outline",
    route: "/member-v2/community",
    disabled: false,
  },
  {
    title: "Mis Beneficios",
    icon: "mdi-hand-coin-outline",
    route: "/member-v2/benefits",
    disabled: false,
  },
  {
    title: "Productos y Servicios",
    icon: "mdi-shopping-outline",
    route: "/member-v2/products",
    disabled: false,
  },
  {
    title: "Academia",
    icon: "mdi-school",
    route: ACADEMY_ROUTE,
    disabled: false,
    external: true,
    devOnly: true,
  },
];

export const IMEF_DIRECTORY_ITEM = {
  title: "Directorio",
  icon: "mdi-book-open-page-variant",
  route: "/member-v2/directory",
  disabled: false,
  external: false,
};

const PRODUCT_SERVICES_ROUTE = "/member-v2/products";

const INACTIVE_MEMBER_ALLOWED_ROUTES = [
  "/member-v2/home",
  "/member-v2/profile",
  "/member-v2/history",
  "/member-v2/events",
];

const isMembersUser = (user) =>
  Boolean(user) && (user.role === "MEMBERSHIP_USER" || user.role === "MEMBER");

const isTitular = (user) =>
  Boolean(user) && (user.role === "MEMBER" || user.role === "MEMBERSHIP_USER");

const orgOwnerOf = (user) => {
  if (!user) return null;
  if (user.headOffice && typeof user.headOffice === "object") return user.headOffice;
  return user;
};

const orgAllowsSubmembers = (user) => {
  const org = orgOwnerOf(user);
  return org ? org.allowSubmembers !== false : true;
};

const isAffiliationAffiliateSub = (user) => {
  if (!user) return false;
  const credentialInformation =
    user?.credential && user.credential.credentialInformation;
  if (!credentialInformation || !credentialInformation.externalId) return false;
  return String(credentialInformation.externalId).substring(0, 2) === "AF";
};

const isAsofomUser = (user) => {
  if (!user) return false;
  return isOrganization(
    "asofom",
    user._id,
    typeof user.headOffice === "object" ? user.headOffice?._id || user.headOffice?.id : user.headOffice,
    user.organization
  );
};

const resolveMembersNavigation = (user) => {
  if (!isTitular(user)) return [];

  const { affiliationType: nationalAffiliation } = retrieveMemberAffiliationDetails({
    user,
    affiliationLevel: "national",
  });

  const allowSubmembers = orgAllowsSubmembers(user);
  const enableSublevel = Boolean(nationalAffiliation && nationalAffiliation.enableSublevel);
  const showSac = isAsofomUser(user);

  let items = MEMBERS_NAVIGATION.filter(
    (item) => !(item.route === "/member-v2/affiliates" && (!enableSublevel || !allowSubmembers))
  );

  // El autodiagnóstico SAC es exclusivo para socios de ASOFOM
  if (!showSac) {
    items = items.filter((item) => item.route !== "/sac");
  }

  items = items.filter((item) => {
    if (item.devOnly && !isDevelopmentEnvironment()) return false;
    if (item.prodAsofomOnly && !canAccessVoting(user)) return false;
    if (item.asocoinsOnly && !canAccessAsocoins(user)) return false;
    if (item.route === PRODUCT_SERVICES_ROUTE && !canAccessProductServices(user)) {
      return false;
    }
    return true;
  });

  if (user.validationStatus === "ACTIVE") {
    const headOfficeId = getEntityId(user.headOffice);
    if (isOrganization("imef", user._id, headOfficeId, user.organization)) {
      if (!items.some((it) => it.route === IMEF_DIRECTORY_ITEM.route)) {
        items = [...items, IMEF_DIRECTORY_ITEM];
      }
    }
    return items;
  }

  return items.map((item) => ({
    ...item,
    disabled: !INACTIVE_MEMBER_ALLOWED_ROUTES.includes(item.route),
  }));
};

const resolveMembershipUsersNavigation = (user) => {
  if (!user || user.role !== "MEMBERSHIP_USER") return [];

  const showSac = isAsofomUser(user);

  let baseNav = MEMBERSHIP_USERS_NAVIGATION.filter((item) => {
    if (item.devOnly && !isDevelopmentEnvironment()) return false;
    if (item.prodAsofomOnly && !canAccessVoting(user)) return false;
    if (item.asocoinsOnly && !canAccessAsocoins(user)) return false;
    if (item.route === PRODUCT_SERVICES_ROUTE && !canAccessProductServices(user)) {
      return false;
    }
    return true;
  });

  // El autodiagnóstico SAC es exclusivo para socios de ASOFOM
  if (!showSac) {
    baseNav = baseNav.filter((item) => item.route !== "/sac");
  }

  if (user.validationStatus === "ACTIVE") {
    return baseNav;
  }

  return baseNav.map((item) => ({
    ...item,
    disabled: !INACTIVE_MEMBER_ALLOWED_ROUTES.includes(item.route),
  }));
};

export const resolveMemberNavItems = (user) => {
  if (!user) return [];

  const isAffiliateCredential = isAffiliationAffiliateSub(user);
  let items = [];

  if (user.role === "MEMBERSHIP_USER" || isAffiliateCredential) {
    items = resolveMembershipUsersNavigation(user);
  } else if (user.role === "MEMBER") {
    items = resolveMembersNavigation(user);
  }

  const communityLabel = getCommunityLabel(user);
  return items.map((item) => (
    item.route === "/member-v2/community" ? { ...item, title: communityLabel } : item
  ));
};
